import "./receptionTypes.js";
import { Box, Card, CardContent, Typography } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  appointmentStep,
  status,
  TREATED_NO_SHOW_M,
} from "shared/src/appointment.mjs";
import { TreatedClientHeader } from "./TreatedClientHeader";
import { TreatedClient } from "./TreatedClient";
import * as d from "shared/src/date.mjs";
import { msToM } from "../resource-list/fn.mjs";

/**
 * @param {Appointment} event
 *
 * @returns {Date | null}
 */
function getFieldChangedAt(event, field, to = null) {
  const data = event.audit.data;

  const pred =
    to == null
      ? (a) => a.changed_fields?.[field] != null
      : (a) => a.changed_fields?.[field] === to;

  const last = data.findLast(pred);
  if (last != null) {
    return d.parseUtcDateTime(last.created_at);
  }

  const first = data[0];
  if (
    to == null
      ? first?.row_data?.[field] != null
      : first?.row_data?.[field] === to
  ) {
    return d.parseUtcDateTime(first.created_at);
  }
}

function getTreatedInDate(appointment) {
  if (
    appointment.start === appointment.end ||
    appointment.service?.type === "consultation"
  ) {
    return getFieldChangedAt(appointment, "status_id", String(status.FINISHED));
  }

  return getFieldChangedAt(
    appointment,
    "active_step",
    appointmentStep.PROCEDURE_NOTES,
  );
}

function treatedIn(now, appointment) {
  const date = getTreatedInDate(appointment);

  if (date == null) {
    return null;
  }

  const diff = now - date;

  return msToM(diff);
}

/**
 * A component to display a list of treated clients
 *
 * @typedef {object} TreatedClientListProps
 * @property {Array<Appointment} appointments
 * @property {function} setReservationDialogOptions Set the options for the reservation dialog
 * @property {function} setIsHovered Setter function for the isHovered state
 * @property {string} hoverOverId
 * @property {function} setHoverOverId Setter function for the hoverOverId state
 * @param {number} order_from_h Number of hours in advance a follow-up appointment can be booked
 *
 * @param {TreatedClientListProps} props
 * @returns {ReactElement}
 */
export const TreatedClientList = ({
  appointments,
  setReservationDialogOptions,
  setIsHovered,
  hoverOverId,
  setHoverOverId,
  order_from_h,
}) => {
  const [treatedClients, setTreatedClients] = useState(appointments);
  const { t } = useTranslation();

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  useEffect(() => {
    const now = new Date();

    setTreatedClients(
      appointments
        .filter(
          (appointment) =>
            appointment.parent_id == null &&
            appointment.adhoc_type !== "CHAIR" &&
            (appointment.status_id === status.TREATED ||
              appointment.status_id === status.FINISHED) &&
            appointment.followup_id == null &&
            (treatedIn(now, appointment) ?? Number.MAX_SAFE_INTEGER) <
              TREATED_NO_SHOW_M,
        )
        .sort(
          (a, b) =>
            new Date(
              a.audit.data.findLast(
                (change) =>
                  change?.changed_fields?.status_id === String(status.TREATED),
              )?.created_at,
            ).getTime() -
            new Date(
              b.audit.data.findLast(
                (change) =>
                  change?.changed_fields?.status_id === String(status.TREATED),
              )?.created_at,
            ).getTime(),
        ),
    );
  }, [appointments]);

  return (
    <Card onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h2" gutterBottom>
            {t("reception.treated_clients")}
          </Typography>
        </Box>

        <TreatedClientHeader />

        {treatedClients.map((appointment) => (
          <TreatedClient
            key={appointment.id}
            appointment={appointment}
            setReservationDialogOptions={setReservationDialogOptions}
            hoverOverId={hoverOverId}
            setHoverOverId={setHoverOverId}
            order_from_h={order_from_h}
          />
        ))}
      </CardContent>
    </Card>
  );
};
